import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { ToasterComponent } from 'src/app/components/common/toaster/toaster.component';
import { TranslateKeyPipe } from 'src/app/pipes/translate-key/translate-key.pipe';
import { makeAttendant } from 'src/data/services/registers/attendants/attendants-factory';
import { IAttendants } from 'src/data/services/registers/attendants/attendants';
import { makePriorityService } from 'src/data/services/registers/priorities/priority-service-factory';
import { IPriorityService } from 'src/data/services/registers/priorities/priority-service';
import { makeResolutionService } from 'src/data/services/registers/resolutions/resolution-service-factory';
import { IResolutionService } from 'src/data/services/registers/resolutions/resolution-service';
import { makeServiceGroupService } from 'src/data/services/registers/service-groups/service-group-service-factory';
import { IServiceGroupService } from 'src/data/services/registers/service-groups/service-group-service';
import { makeService } from 'src/data/services/registers/services/service-factory';
import { IService } from 'src/data/services/registers/services/service';
import { CustomError } from 'src/helpers/error/custom-error';
import { showPublicErrorToasts } from 'src/helpers/error/show-public-error-toasts';
import { LanguageKey } from 'src/main/translate/language-key/language-key';
import { CustomErrorTitle } from 'src/models/common/error/custom-error-title/custom-error-title';
import { Entity } from 'src/models/entity/entity';
import { makeTranslateService } from 'src/data/services/common/translate/translate-service-factory';

type TargetEntityOption = {
  id: string;
  name: string;
  level: number;
};

export type PostCreateEntityLinksCategoryKey =
  | 'services'
  | 'priorities'
  | 'resolutions'
  | 'service-groups'
  | 'attendants';

@Component({
  selector: 'app-post-create-entity-links-dialog',
  standalone: true,
  templateUrl: './post-create-entity-links-dialog.component.html',
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    CheckboxModule,
    ButtonModule,
    ToasterComponent,
    TranslateKeyPipe,
  ],
})
export class PostCreateEntityLinksDialogComponent implements OnChanges {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly translateService = makeTranslateService();
  private readonly serviceApi: IService = makeService();
  private readonly priorityApi: IPriorityService = makePriorityService();
  private readonly resolutionApi: IResolutionService = makeResolutionService();
  private readonly serviceGroupApi: IServiceGroupService =
    makeServiceGroupService();
  private readonly attendantApi: IAttendants = makeAttendant();

  @Input() visible = false;
  @Input() rootEntity: Entity | null = null;
  @Input() itemId = '';
  @Input() itemName = '';
  @Input() categoryKey: PostCreateEntityLinksCategoryKey = 'services';

  @Output() finished = new EventEmitter<void>();

  @ViewChild(ToasterComponent) public toaster!: ToasterComponent;

  public readonly languageKey = LanguageKey;
  public loading = false;
  public saving = false;
  public targetEntities: TargetEntityOption[] = [];
  public selectedTargetEntityIds = new Set<string>();
  private initializedSignature = '';
  private linkedItemIdsByEntityId = new Map<string, Set<string>>();

  get itemTypeKey(): LanguageKey {
    switch (this.categoryKey) {
      case 'priorities':
        return LanguageKey.PRIORITY;
      case 'resolutions':
        return LanguageKey.RESOLUTION;
      case 'service-groups':
        return LanguageKey.SERVICE_GROUP;
      case 'attendants':
        return LanguageKey.ATTENDANT;
      default:
        return LanguageKey.SERVICE;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.visible) {
      this.resetState(false);
      return;
    }

    if (
      changes['visible'] ||
      changes['rootEntity'] ||
      changes['itemId'] ||
      changes['categoryKey']
    ) {
      void this.initialize();
    }
  }

  isSelected(entityId: string): boolean {
    return this.selectedTargetEntityIds.has(entityId);
  }

  toggleEntity(entityId: string, checked: boolean): void {
    const nextSelection = new Set(this.selectedTargetEntityIds);

    if (checked) {
      nextSelection.add(entityId);
    } else {
      nextSelection.delete(entityId);
    }

    this.selectedTargetEntityIds = nextSelection;
  }

  selectAll(): void {
    this.selectedTargetEntityIds = new Set(
      this.targetEntities.map((entity) => entity.id),
    );
  }

  clearSelection(): void {
    this.selectedTargetEntityIds = new Set<string>();
  }

  async save(): Promise<void> {
    if (!this.rootEntity?.id || !this.itemId || this.targetEntities.length === 0) {
      this.finishFlow();
      return;
    }

    this.saving = true;
    this.detectChanges();

    try {
      await Promise.all(
        this.targetEntities.map(async (targetEntity) => {
          const currentLinkedIds = new Set(
            this.linkedItemIdsByEntityId.get(targetEntity.id) ?? [],
          );
          const shouldBeLinked = this.selectedTargetEntityIds.has(targetEntity.id);
          const wasLinked = currentLinkedIds.has(this.itemId);

          if (shouldBeLinked === wasLinked) {
            return;
          }

          if (shouldBeLinked) {
            currentLinkedIds.add(this.itemId);
          } else {
            currentLinkedIds.delete(this.itemId);
          }

          await this.syncEntityLinks(targetEntity.id, [...currentLinkedIds]);
          this.linkedItemIdsByEntityId.set(targetEntity.id, currentLinkedIds);
        }),
      );

      this.toaster.success(
        CustomErrorTitle.EMPTY,
        this.translateService.translateKey(
          LanguageKey.LINKS_SAVED_SUCCESSFULLY,
        ),
      );
      this.finishFlow();
    } catch (error) {
      this.handleError(error);
    } finally {
      this.saving = false;
      this.detectChanges();
    }
  }

  skip(): void {
    this.finishFlow();
  }

  private async initialize(): Promise<void> {
    const signature = [
      this.rootEntity?.id ?? '',
      this.itemId,
      this.categoryKey,
    ].join(':');

    if (!this.visible || !this.rootEntity?.id || !this.itemId) {
      this.resetState(false);
      return;
    }

    if (signature === this.initializedSignature) {
      return;
    }

    this.initializedSignature = signature;
    this.targetEntities = this.flattenDescendants(this.rootEntity.children ?? []);
    this.selectedTargetEntityIds = new Set<string>();
    this.linkedItemIdsByEntityId = new Map<string, Set<string>>();

    if (this.targetEntities.length === 0) {
      this.detectChanges();
      return;
    }

    this.loading = true;
    this.detectChanges();

    try {
      const linkResponses = await Promise.all(
        this.targetEntities.map((entity) => this.listLinkedItemIds(entity.id)),
      );
      const nextSelection = new Set<string>();

      this.targetEntities.forEach((entity, index) => {
        const linkedIds = new Set(linkResponses[index]);
        this.linkedItemIdsByEntityId.set(entity.id, linkedIds);

        if (linkedIds.has(this.itemId)) {
          nextSelection.add(entity.id);
        }
      });

      this.selectedTargetEntityIds = nextSelection;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loading = false;
      this.detectChanges();
    }
  }

  private flattenDescendants(
    entities: Entity[],
    level: number = 0,
  ): TargetEntityOption[] {
    return entities.flatMap((entity) => [
      {
        id: entity.id ?? '',
        name: entity.name ?? '',
        level,
      },
      ...this.flattenDescendants(entity.children ?? [], level + 1),
    ]);
  }

  private async listLinkedItemIds(entityId: string): Promise<string[]> {
    switch (this.categoryKey) {
      case 'priorities': {
        const links = await this.priorityApi.listEntityLinks(entityId);
        return (links ?? [])
          .map((link) => link.priority_id?.toString().trim())
          .filter((linkId): linkId is string => !!linkId);
      }
      case 'resolutions': {
        const links = await this.resolutionApi.listEntityLinks(entityId);
        return (links ?? [])
          .map((link) => link.resolution_id?.toString().trim())
          .filter((linkId): linkId is string => !!linkId);
      }
      case 'service-groups': {
        const links = await this.serviceGroupApi.listEntityLinks(entityId);
        return (links ?? [])
          .map((link) => link.service_group_id?.toString().trim())
          .filter((linkId): linkId is string => !!linkId);
      }
      case 'attendants': {
        const links = await this.attendantApi.listEntityLinks(entityId);
        return (links ?? [])
          .map((link) => link.attendant_id?.toString().trim())
          .filter((linkId): linkId is string => !!linkId);
      }
      default: {
        const links = await this.serviceApi.listEntityLinks(entityId);
        return (links ?? [])
          .map((link) => link.service_id?.toString().trim())
          .filter((linkId): linkId is string => !!linkId);
      }
    }
  }

  private async syncEntityLinks(
    entityId: string,
    linkedIds: string[],
  ): Promise<void> {
    if (!this.rootEntity?.id) {
      return;
    }

    switch (this.categoryKey) {
      case 'priorities':
        await this.priorityApi.syncEntityLinks(entityId, {
          owner_root_entity_id: this.rootEntity.id,
          priority_ids: linkedIds,
        });
        return;
      case 'resolutions':
        await this.resolutionApi.syncEntityLinks(entityId, {
          owner_root_entity_id: this.rootEntity.id,
          resolution_ids: linkedIds,
        });
        return;
      case 'service-groups':
        await this.serviceGroupApi.syncEntityLinks(entityId, {
          owner_root_entity_id: this.rootEntity.id,
          service_group_ids: linkedIds,
        });
        return;
      case 'attendants':
        await this.attendantApi.syncEntityLinks(entityId, {
          owner_root_entity_id: this.rootEntity.id,
          attendant_ids: linkedIds,
        });
        return;
      default:
        await this.serviceApi.syncEntityLinks(entityId, {
          owner_root_entity_id: this.rootEntity.id,
          service_ids: linkedIds,
        });
    }
  }

  private finishFlow(): void {
    this.visible = false;
    this.resetState(false);
    this.finished.emit();
  }

  private resetState(keepVisibility: boolean): void {
    this.loading = false;
    this.saving = false;
    this.targetEntities = [];
    this.selectedTargetEntityIds = new Set<string>();
    this.linkedItemIdsByEntityId = new Map<string, Set<string>>();
    this.initializedSignature = '';

    if (!keepVisibility) {
      this.visible = false;
    }

    this.detectChanges();
  }

  private handleError(error: unknown): void {
    const customError = CustomError.handleError(
      'PostCreateEntityLinksDialogComponent -> error',
      error,
    );

    if (!this.toaster) {
      return;
    }

    showPublicErrorToasts({
      publicErrors: customError.getPublicError(),
      toaster: this.toaster,
      translateService: this.translateService,
      defaultMessageKey:
        this.languageKey.AN_ERROR_HAS_OCCURRED_WHILE_PROCESSING_YOUR_REQUEST,
    });
    this.detectChanges();
  }

  private detectChanges(): void {
    this.changeDetectorRef.detectChanges();
  }
}
